import React, { useEffect, useState } from "react";
import { Minus, Plus, Search, ShoppingCart, X } from "lucide-react";
// import { toast } from "react-toastify";
import { useProductStore } from "../../store/useProductStore";
import { useAuthStore } from "../../store/useAuthStore";
import AddProductModal from "./AddProductModal"; 
import { Pagination, Stack } from "@mui/material";
import { toast } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import orderService from "../../services/order.service";
import { useDebounce } from "../../hooks/useDebounce";

const AddProductTable = () => {
    const { user } = useAuthStore();
    const location = useLocation();
    const navigate = useNavigate();
    const orderId = location.state?.order_id || null;
    if(!orderId) navigate('/order-list');
    
    const USER_ROLE = user?.userDataObj?.role;
    const { fetchProducts, setCartCount, fetchSkuMakeandSegment, fetchPartNumber } = useProductStore();
    const [products, setProducts] = useState([]);
    const [currentOrderId, setCurrentOrderId] = useState(orderId);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [totalCount, setTotalCount] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const cartCount = useProductStore((state) => state.cartCount);
    const [subOrders, setSubOrders] = useState([]);
    const [filter, setFilter] = useState({
        limit: 10,
        offset: 1,
        make_id: "",
        segment_id: "",
        size_id: "",
        skuNameAndPartNoSearch: ""
    })
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [searchResults, setSearchResults] = useState([]);
    const [make, setMake] = useState([])
    const [size, setSize] = useState([])
    const [segment, setSegment] = useState([])

    const fetchData = async (filter) => {
        setIsLoading(true);
        filter.order_id = orderId;
        const res = await fetchProducts(filter);

        if (res.status) {
            setTotalPages(res.data.pagination.totalPages);
            setTotalCount(res.data.count);
            setProducts(res.data.rows);
        } else {
            setTotalPages(1);
            setTotalCount(0);
            setProducts([]);
        }
        setIsLoading(false);
    };

    const fetchSkuMakeandSegmentData = async () => {
        const res = await fetchSkuMakeandSegment();

        if (res.status) {
            const { make, segment, size } = res.data;
            setMake(make);
            setSegment(segment);
            setSize(size);
        } else {
            setMake([]);
            setSegment([]);
            setSize([]);
        }

    };

    // State to manage cart data
    const [cartData, setCartData] = useState([]);

    useEffect(() => {
        // Update cart count whenever cartData changes
        setCartCount(cartData.filter(value => value?.ordered_quantity > 0).length);
    }, [cartData, setCartCount]);

    // Get the product quantity from the cart
    const getProductQuantity = (productId) => {
        const product = cartData.find((item) => item.product_id === productId);
        return product ? product.ordered_quantity : -1;
    };

    // Handle Add to Cart
    const handleAddToCart = (product) => {      
        const updatedCart = [
            ...cartData,
            { ...product, ordered_quantity: 1, product_id: product.id },
        ];
        // console.log("updatedCart",updatedCart);        
        setCartData(updatedCart);
    };

    // Increase quantity of a product in the cart
    const handleIncrease = (productId) => {
        const updatedCart = cartData.map((item) =>
          item.product_id === productId 
              ? { ...item, ordered_quantity: (item.ordered_quantity + 1) > 1000 ? 1000 : (item.ordered_quantity + 1) }
              : item
        );       
        // localStorage.setItem("product_data", JSON.stringify(updatedCart));
        setCartData(updatedCart); // Update cart data in state
    };

    const handleInputChange = (value, productId) => {
      // console.log("value, productId",value, productId);
      if (value > 1000) {
            toast.dismiss();
            return toast.info('Quantity exceed. Maximum allowed 1000.', { toastId: 'quantityLimit' });
          }
      
      if (value === "") {
        const updatedCart = cartData.map((item) =>
          item.product_id === productId
              ? { ...item, ordered_quantity: 0 }
              : item
      );
    //   localStorage.setItem("product_data", JSON.stringify(updatedCart));
      setCartData(updatedCart);
      } else {
        const newQuantity = Math.max(0, parseInt(value, 10) || 0);
        const updatedCart = cartData.map((item) =>
          item.product_id === productId
              ? { ...item, ordered_quantity: newQuantity }
              : item
      );
    //   localStorage.setItem("product_data", JSON.stringify(updatedCart));
      setCartData(updatedCart);
      }
    };

    // Decrease quantity of a product in the cart
    const handleDecrease = (productId) => {
        const updatedCart = cartData
            .map((item) =>
                item.product_id === productId
                    ? {
                        ...item,
                        ordered_quantity: Math.max(-1, item.ordered_quantity - 1),
                    }
                    : item
            )
            .filter((item) => item.ordered_quantity > 0); 
        setCartData(updatedCart); 
    };

    const handleConfirmOrder = () => {
        // if (cartData.length === 0) {
        //   toast.error(
        //     "Your cart is empty. Please add products before confirming the order."
        //   );
        // } else {
        //   setIsModalOpen(true);
        // }

        setIsModalOpen(true);
    };

    const getAvailability = (stock_quantity) => {
        // Logic for determining availability status based on stock quantity
        if (stock_quantity > 500) {
            return {
                status: "In Stock",
                bgClass: "bg-emerald-100 text-emerald-800", // Green background for "In Stock"
            };
        } else if (stock_quantity > 0 && stock_quantity <= 500) {
            return {
                status: "Limited Stock",
                bgClass: "bg-orange-100 text-orange-800", // Orange background for "Limited Stock"
            };
        } else {
            return {
                status: "Available Soon",
                bgClass: "bg-red-100 text-red-800", // Red background for "Available Soon"
            };
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        setFilter({ ...filter, offset: pageNumber })
    };

    const handleSearch = () => {
        const { make_id, skuNameAndPartNoSearch, segment_id, size_id } = filter;
        if (skuNameAndPartNoSearch == "" && segment_id == "" && make_id == "" && size_id == "") {
            return;
        }

        setCurrentPage(1);
        filter.offset = 1;
        fetchData(filter);
    }

    const handleReset = () => {
        setFilter({
            limit: 10,
            offset: 1,
            make_id: "",
            segment_id: "",
            size_id: "",
            skuNameAndPartNoSearch: ""
        })
        setCurrentPage(1)
        fetchData({ limit: 10, offset: 1});
    }

    const fetchSubOrders = async () => {
        setIsLoading(true);
        try { 
            const params = {order_id : currentOrderId, status_id : [4]};
            const response = await orderService.getSkuOrderList(params);
            // console.log("rrrrrrrr",response);
            if(response.status){
                setSubOrders(response.data);
            }else{
                setSubOrders([]);
                toast.error('Fail to fetch sku details.', {toastId: 'errorMessageFetchSubOrders'});
            }
         }catch (error) {
            setSubOrders([]);
            toast.error(error.message || 'Fail to fetch sku details.', {toastId: 'errorMessageFetchSubOrders'});
         }finally{setIsLoading(false)}
    }

    useEffect(() => {
        fetchData(filter);
    }, [currentPage]);

    useEffect(() => {
        fetchSkuMakeandSegmentData();
        fetchSubOrders();
    }, []);

    useEffect(() => {
        fetchData({...filter, offset : 1});
        setFilter({ ...filter, offset: 1 })
        setCurrentPage(1);
    }, [filter.category_id, filter.make_id, filter.segment_id, filter.size_id]);

    const fetchPartNumberData = async (debouncedSearchTerm) => {
      const body = {
        part_no: debouncedSearchTerm,
        order_id : orderId
      };
      const res = await fetchPartNumber(body);
      if (res.status) {
        if (res.data?.length > 1) {
          setSearchResults(res.data);
        } else {
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
      }
    };

    useEffect(() => {
      if (debouncedSearchTerm) {
          fetchPartNumberData(debouncedSearchTerm);
      }
  }, [debouncedSearchTerm]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchData({...filter, offset : 1});
      setFilter({ ...filter, offset: 1 });
      setCurrentPage(1);
      setSearchResults([]);
    }
  };
      if(isLoading){
        return <Loader />
      }

      console.log(cartData, "cartData");

    return (
      <>
        <div className="overflow-x-auto">
          <div className="flex flex-col md:flex-row items-center mb-4 space-y-2 md:space-y-0 md:space-x-4">
            <div className="relative w-full md:w-[600px]">
              <Search className="absolute left-1 top-2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={filter.skuNameAndPartNoSearch}
                onChange={(e) => {
                    if (searchTerm !== e.target.value) setSearchTerm(e.target.value);
                    setFilter({ ...filter, skuNameAndPartNoSearch: e.target.value })
                  }
                }
                onKeyUp={handleKeyPress}
                placeholder="Search by Part Number"
                className="w-full pl-8 pr-1 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
              />
              {searchResults.length > 0 && searchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setFilter(prev => ({ ...prev, skuNameAndPartNoSearch: result.part_no }));
                        fetchData({...filter, skuNameAndPartNoSearch: result.part_no});
                        setSearchResults([]);
                      }}
                    >
                      {result.part_no}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row md:items-center w-full">
              <div className="grid grid-cols-1 md:flex md:space-x-2 w-full gap-1 custom-grid">
                            <select className="border border-gray-300 rounded-lg px-2 py-1 w-full md:w-auto"
                  value={filter.segment_id}
                                onChange={(e) => setFilter({ ...filter, segment_id: e.target.value })}>
                  <option value="">All Segment</option>
                                {
                                    segment.map((data) => {
                                        return <option key={data.id} value={data.id}>{data.segment}</option>
                                    })
                                }

                </select>
                            <select className="border border-gray-300 rounded-lg px-2 py-1 w-full md:w-auto" value={filter.size_id}
                                onChange={(e) => setFilter({ ...filter, size_id: e.target.value })}>
                  <option value="">All Size</option>
                                {
                                    size.map((data) => {
                                        return <option key={data.id} value={data.id}>{data.size}</option>
                                    })
                                }
                </select>

                            <select className="border border-gray-300 rounded-lg px-2 py-1 w-full md:w-auto" value={filter.make_id}
                                onChange={(e) => setFilter({ ...filter, make_id: e.target.value })}>
                  <option value="">All Make</option>
                                {
                                    make.map((data) => {
                                        return <option key={data.id} value={data.id}>{data.make}</option>
                                    })
                                }
                </select>

                            {/* <button className="w-full md:w-auto px-2 py-1 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 transition" onClick={handleSearch}>
                  Apply
                </button> */}

                <button
                                className="w-full md:w-auto px-2 py-1 border-2 text-black rounded-lg transition" onClick={handleReset}>
                  Reset
                </button>

                {(cartCount > 0 && USER_ROLE === 3)  && (
                    <div className="flex justify-end w-full">
                        <button
                        onClick={handleConfirmOrder}
                        className="flex items-center px-4 py-2 bg-emerald-300 text-black rounded-lg hover:bg-primary-dark transition"
                        >
                        Confirm Order
                        <div className="relative flex items-center ml-2">
                            <ShoppingCart className="h-6 w-6 text-gray-500" />
                            <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {cartCount}
                            </span>
                        </div>
                        </button>
                    </div>
                    )}

              </div>
            </div>
          </div>

          <div className="overflow-auto h-[250px] sm:h-[400px] md:h-[450px] xl:h-[600px] rounded-lg">
            <table className="min-w-full overflow-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-emerald-200">
                 <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Part Number
                  </th>
                  {/* <th className="p-3 text-left text-sm font-bold text-gray-700">
                    SKU Name
                  </th> */}
                  <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Segment
                  </th>
                  <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Size
                  </th>
                  <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Make
                  </th>
                  <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Price (₹)
                  </th>
                  <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Sets
                  </th>
                  {USER_ROLE === 4 && <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Availability
                  </th>}
                  {USER_ROLE === 3 && <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Add to Cart ({(cartData || []).reduce((accum, item) => accum + (item.ordered_quantity * item.no_of_sets || 0), 0)})
                  </th>}
                  {USER_ROLE === 3 && <th className="p-3 text-left text-sm font-bold text-gray-700">
                    Total MRP (₹)
                  </th>}
                  {USER_ROLE === 4 && <th className="p-3 text-center text-sm font-bold text-gray-700">
                    Production
                  </th>}
                  {USER_ROLE === 4 && <th className="p-3 text-center text-sm font-bold text-gray-700">
                    Dispatched
                  </th>}
                  {USER_ROLE === 4 && <th className="p-3 text-center text-sm font-bold text-gray-700">
                    Balance Sets
                  </th>}
                  {USER_ROLE === 4 && <th className="p-3 text-center text-sm font-bold text-gray-700">
                    Order Request
                  </th>}
                  {USER_ROLE === 4 && <th className="p-3 text-center text-sm font-bold text-gray-700">
                    Forecast for Next Month
                  </th>}
                </tr>
              </thead>
                        {products.length != 0 ?
                            (<tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product, index) => {
                    const quantity = getProductQuantity(product.id);
                                    const availability = getAvailability(product.available_stock);
                    return (
                      <tr
                        key={product.id}
                        className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                      >
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.part_no}
                        </td>
                        {/* <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.sku_name}
                        </td> */}
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.segment_name}
                        </td>
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.size}
                        </td>
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.make}
                        </td>
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                          {product.default_mrp}
                        </td>
                        <td className="p-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {product.no_of_sets}
                        </td>
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 rounded-full ${availability.bgClass}`}
                          >
                            {availability.status}
                          </span>
                        </td>}
                        {USER_ROLE === 3 && <td className="p-3 whitespace-nowrap">
                                                {
                                                    quantity < 0 ? (
                            <button
                              className="px-3 py-1 bg-emerald-500 text-white text-sm rounded hover:bg-emerald-600"
                              onClick={() => handleAddToCart(product)}
                            >
                              Add to Cart
                            </button>
                          ) : (
                            <div className="flex items-center space-x-2">
                              {/* <span className="text-sm font-medium">
                                {quantity}
                              </span> */}
                              {product.no_of_sets} <X size={12} />
                              <input
                                type="number"
                                className="w-12 text-center text-sm font-medium border 
                                rounded p-1 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                value={quantity}
                                onChange={(e) => handleInputChange(e.target.value, product.id)}
                                min="0"
                              />
                              &nbsp; = {cartData.find((item) => item.product_id === product.id)?.ordered_quantity * product.no_of_sets || 0}
                            </div>
                                                    )
                                                }
                        </td>}
                        {USER_ROLE === 3 && <td className="p-3 whitespace-nowrap text-center text-sm text-gray-900">
                          {product.no_of_sets * product.default_mrp * cartData.find((item) => item.product_id === product.id)?.ordered_quantity || 0}
                        </td>}
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                           {product.total_stock}
                        </td>}
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                           {product.sent_stock}
                        </td>}
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                           {product.available_stock}
                        </td>}
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                           {product.total_order || 0}
                        </td>}
                        {USER_ROLE === 4 && <td className="p-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                           {product.next_month_forecast || 0}
                        </td>}
                        
                      </tr>
                    );
                  })}
                            </tbody>) :
                            (<tbody>
                  <tr>
                                    <td className="p-3 whitespace-nowrap text-sm text-center text-gray-900" colSpan={8}>
                      No Products Available
                    </td>
                  </tr>

                            </tbody>)
                        }
              {/* <tfoot>
                            <tr>
                                <td className="p-3 whitespace-nowrap text-sm text-left text-gray-900" colSpan={8}>
                                    Page {currentPage} Of {totalPages || 1}
                                </td>
                            </tr>

                        </tfoot> */}
            </table>

            {totalCount !== null && (
            <div className="flex justify-center mt-2">
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, value) => handlePageChange(value)}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  size="10"
                />
              </Stack>
            </div>
          )}
          </div>

          {/* {totalCount !== null && (
            <div className="flex justify-center mt-2">
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, value) => handlePageChange(value)}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                />
              </Stack>
            </div>
          )} */}
        </div>

        {isModalOpen && (
          <AddProductModal
            closeModal={() => setIsModalOpen(false)}
            subOrders={subOrders}
            cartDataDetails={cartData}
            orderId={orderId}
            Close
            modal
            function
          />
        )}
      </>
    );
};

export default AddProductTable;